import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { jwtHelper } from "../../helpers/JwtHelper";
import isIdyaResult from "../../helpers/isIdyaResult";
import {
  axiosApi,
  clearStoredTokens,
  JWT_TOKEN_KEY,
  refreshTokens,
  RefreshTokenError,
  storeTokens,
  UserToken,
} from "../../interceptors/axios";
import { IdyaJwtPayload } from "../../types/JwtPayload";
import { Result } from "../../types/Result";

export const SESSION_EXPIRED_MESSAGE =
  "Oturumunuzun süresi doldu, lütfen tekrar giriş yapın.";

interface AuthState {
  token: string;
  username: string;
  isLoggedIn: boolean;
  loading: boolean;
  id: string;
  errors: string[];
  professionCode?: string;
  professionName?: string;
  fullName: string;
  exp?: number;
  packages: string[];
  statusCode?: string;
  email: string;
  tc: string;
  currentAssignmentPeriodCode?: string;
  isEarlyStagePremium: boolean;
  referenceCode: string;
  phone?: string;
  roles: string[];
  tokenRefresing: boolean;
}

const initialState: AuthState = {
  token: "",
  fullName: "",
  username: "",
  id: "",
  isLoggedIn: false,
  loading: false,
  errors: [],
  packages: [],
  statusCode: "",
  email: "",
  tc: "",
  referenceCode: "",
  isEarlyStagePremium: false,
  roles: [],
  tokenRefresing: false,
};

const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

/** Token'daki claim'leri state'e yazar. */
function applyClaims(state: AuthState, token: IdyaJwtPayload) {
  state.professionCode = token.professionCode;
  state.fullName =
    token["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"];
  state.username =
    token["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
  state.id =
    token["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
  state.email =
    token["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];
  state.phone =
    token["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/mobilephone"];
  const roles = token[ROLE_CLAIM];
  if (roles) state.roles = Array.isArray(roles) ? roles : [roles];
  state.currentAssignmentPeriodCode = token.currentAssignmentPeriodCode;
  state.isEarlyStagePremium = !!token.isEarlyStagePremium;
  state.exp = token.exp;
  state.tc = token.tc;
  if (token.packages) {
    if (Array.isArray(token.packages)) state.packages = token.packages;
    else if (typeof token.packages === "string")
      state.packages = [token.packages];
  }
  state.referenceCode = token.referenceCode;
  state.professionName = token.professionName;
}

/** Geçerli bir access token'ı state'e uygular; geçersizse false döner. */
function applyToken(state: AuthState, token: string): boolean {
  const valid = jwtHelper.isTokenValid(token, (decoded) =>
    applyClaims(state, decoded)
  );
  if (valid) {
    state.isLoggedIn = true;
    state.token = token;
  }
  return valid;
}

function clearSession(state: AuthState) {
  state.token = "";
  state.fullName = "";
  state.username = "";
  state.id = "";
  state.email = "";
  state.tc = "";
  state.referenceCode = "";
  state.packages = [];
  state.roles = [];
  state.isEarlyStagePremium = false;
  delete state.exp;
  delete state.phone;
  delete state.professionCode;
  delete state.professionName;
  delete state.currentAssignmentPeriodCode;
  state.isLoggedIn = false;
  state.tokenRefresing = false;
}

export const logOutAsync = createAsyncThunk("auth/logout", async () => {
  try {
    await axiosApi.post("api/Auth/logout");
  } catch (error) {
    // API hatası olsa bile oturum yerelde kapatılır
    console.error("Logout error:", error);
  } finally {
    clearStoredTokens();
  }
  return true;
});

export const loginAsync = createAsyncThunk(
  "auth/login",
  async (credentials: { email: string; password: string }, thunkAPI) => {
    try {
      const response = await axiosApi.post<Result<UserToken>>(
        "/api/Auth/login",
        null,
        {
          params: {
            email: credentials.email,
            password: credentials.password,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError)
        return thunkAPI.rejectWithValue(error.response?.data.errors);
      return thunkAPI.rejectWithValue("failed");
    }
  }
);

/**
 * Access token'ı yeniler. Eş zamanlı çağrılar tek bir HTTP isteğini paylaşır
 * (bkz. interceptors/axios.ts refreshTokens). Refresh token geçersizse
 * oturum kapatılır.
 */
export const refreshTokenAsync = createAsyncThunk<
  UserToken,
  void,
  { rejectValue: { silent: boolean; message?: string } }
>("auth/refreshToken", async (_, thunkAPI) => {
  try {
    return await refreshTokens();
  } catch (error) {
    clearStoredTokens();
    if (error instanceof RefreshTokenError) {
      // Refresh token hiç yoksa (ilk açılış) sessizce reddet; varsa ama
      // geçersizse kullanıcıya "oturum süresi doldu" göster.
      const silent = error.result === "Refresh token bulunamadı";
      const message =
        isIdyaResult(error.result) && error.result.errors[0]
          ? error.result.errors[0]
          : SESSION_EXPIRED_MESSAGE;
      return thunkAPI.rejectWithValue({ silent, message });
    }
    return thunkAPI.rejectWithValue({
      silent: false,
      message: SESSION_EXPIRED_MESSAGE,
    });
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ username: string; password: string }>
    ) => {
      state.username = action.payload.username;
    },
    restoreToken: (state, action: PayloadAction<string | null>) => {
      if (action.payload && applyToken(state, action.payload)) {
        localStorage.setItem(JWT_TOKEN_KEY, action.payload);
      } else {
        localStorage.removeItem(JWT_TOKEN_KEY);
      }
      state.errors = [];
      state.loading = false;
    },
    /** Refresh token da geçersiz: oturumu kapat, login'e düş. */
    sessionExpired: (state, action: PayloadAction<string | undefined>) => {
      const wasLoggedIn = state.isLoggedIn;
      clearSession(state);
      state.loading = false;
      state.errors = wasLoggedIn
        ? [action.payload ?? SESSION_EXPIRED_MESSAGE]
        : [];
    },
    logout: (state) => {
      clearSession(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        const tokens = action.payload.data;
        if (tokens && applyToken(state, tokens.token)) {
          storeTokens(tokens);
        }
        state.loading = false;
        state.errors = [];
        state.statusCode = "";
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) state.errors = action.payload;
      })
      .addCase(refreshTokenAsync.pending, (state) => {
        state.tokenRefresing = true;
      })
      .addCase(refreshTokenAsync.fulfilled, (state, action) => {
        state.tokenRefresing = false;
        if (!applyToken(state, action.payload.token)) {
          clearStoredTokens();
          clearSession(state);
        }
      })
      .addCase(refreshTokenAsync.rejected, (state, action) => {
        const wasLoggedIn = state.isLoggedIn;
        clearSession(state);
        if (wasLoggedIn && action.payload && !action.payload.silent)
          state.errors = [action.payload.message ?? SESSION_EXPIRED_MESSAGE];
      })
      .addCase(logOutAsync.pending, (state) => {
        state.loading = true;
        state.errors = [];
        state.statusCode = "";
      })
      .addCase(logOutAsync.fulfilled, (state) => {
        clearSession(state);
        state.loading = false;
      })
      .addCase(logOutAsync.rejected, (state) => {
        clearSession(state);
        state.loading = false;
      });
  },
});

export const { setCredentials, restoreToken, sessionExpired, logout } =
  authSlice.actions;
export default authSlice.reducer;
